import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { getCampaignTimelineData } from "./schema/campaign-timeline/get-campaign-timeline-data";
import { claimConversation } from "./schema/conversations/claim-conversation";
import { getAllQueueCounts } from "./schema/conversations/get-all-queue-counts";
import { getConversationById } from "./schema/conversations/get-conversation-by-id";
import { getConversationLogs } from "./schema/conversations/get-conversation-logs";
import { getConversationMessages } from "./schema/conversations/get-conversation-messages";
import { getQueueConversations } from "./schema/conversations/get-queue-conversations";
import { hasDiscountConfigured } from "./schema/conversations/has-discount-configured";
import { releaseConversationClaim } from "./schema/conversations/release-conversation-claim";
import { renewConversationClaims } from "./schema/conversations/renew-conversation-claims";
import { listPendingEmailCorrections } from "./schema/email-corrections/list-pending-email-corrections";
import { resolveEmailCorrection } from "./schema/email-corrections/resolve-email-correction";
import { debugEmailTemplate } from "./schema/email-templates/debug-email-template";
import { createMarketingAgentPrompt } from "./schema/marketing-agent-prompt/create-marketing-agent-prompt";
import { getAllMarketingAgentPrompts } from "./schema/marketing-agent-prompt/get-all-marketing-agent-prompts";
import { updateMarketingAgentPrompt } from "./schema/marketing-agent-prompt/update-marketing-agent-prompt";
import { getActiveMarketingAgentSplits } from "./schema/marketing-agent/get-active-marketing-agent-splits";
import { getMarketingAgentChannelDistribution } from "./schema/marketing-agent/get-marketing-agent-channel-distribution";
import { getMarketingAgentDecisionDetails } from "./schema/marketing-agent/get-marketing-agent-decision-details";
import { getMarketingAgentDecisionTrends } from "./schema/marketing-agent/get-marketing-agent-decision-trends";
import { getMarketingAgentDecisions } from "./schema/marketing-agent/get-marketing-agent-decisions";
import { getMarketingAgentKeyMetrics } from "./schema/marketing-agent/get-marketing-agent-key-metrics";
import { getMarketingAgentLlmCostMetrics } from "./schema/marketing-agent/get-marketing-agent-llm-cost-metrics";
import { getMarketingAgentLlmCostTrends } from "./schema/marketing-agent/get-marketing-agent-llm-cost-trends";
import { getMarketingAgentPerformanceMetrics } from "./schema/marketing-agent/get-marketing-agent-performance-metrics";
import { getMarketingAgentSplitTestMessageStats } from "./schema/marketing-agent/get-marketing-agent-split-test-message-stats";
import { getMarketingAgentSplitTestRevenueChart } from "./schema/marketing-agent/get-marketing-agent-split-test-revenue-chart";
import { getMarketingAgentSplitTestRevenueStats } from "./schema/marketing-agent/get-marketing-agent-split-test-revenue-stats";
import { getMarketingAgentSplitTestStatisticalSignificance } from "./schema/marketing-agent/get-marketing-agent-split-test-statistical-significance";
import { getMarketingAgentTagDistribution } from "./schema/marketing-agent/get-marketing-agent-tag-distribution";
import { getMarketingAgentTriggerEventDistribution } from "./schema/marketing-agent/get-marketing-agent-trigger-event-distribution";
import { getMarketingAgentWriterMessageTrends } from "./schema/marketing-agent/get-marketing-agent-writer-message-trends";
import { fetchDiscountInfo } from "./schema/messages/fetch-discount-info";
import { flagDraftMessage } from "./schema/messages/flag-draft-message";
import { getReplyToEmail } from "./schema/messages/get-reply-to-email";
import { regenerateDraftMessage } from "./schema/messages/regenerate-draft-message";
import {
  adminSendDraftMessage,
  contractorSendDraftMessage,
} from "./schema/messages/send-draft-message";
import {
  adminSendManualMessage,
  contractorSendManualMessage,
} from "./schema/messages/send-manual-message";
import { skipDraftMessage } from "./schema/messages/skip-draft-message";
import {
  getCurrentMarketingPermissions,
  listMarketingPermissionUsers,
  removeMarketingPermissionUser,
  setMarketingPermissionUser,
} from "./schema/permissions/marketing-permissions";
import { getHumanQueueAgentActivity } from "./schema/recover/get-human-queue-agent-activity";
import { getRecoverAnalytics } from "./schema/recover/get-recover-analytics";
import { getRecoverAutomationMetrics } from "./schema/recover/get-recover-automation-metrics";
import { getRecoverConversationReturn } from "./schema/recover/get-recover-conversation-return";
import { getRecoverDailyMetrics } from "./schema/recover/get-recover-daily-metrics";
import { getRecoverDiscountSplitMetrics } from "./schema/recover/get-recover-discount-split-metrics";
import { getRecoverImessageMerchants } from "./schema/recover/get-recover-imessage-merchants";
import { getRecoverInternalNotes } from "./schema/recover/get-recover-internal-notes";
import { getRecoverMerchantNotes } from "./schema/recover/get-recover-merchant-notes";
import { getRecoverTemplates } from "./schema/recover/get-recover-templates";
import { reportRecoverIssue } from "./schema/recover/report-recover-issue";
import { searchRecoverTeams } from "./schema/recover/search-recover-teams";
import { unsubscribeRecoverForPhoneNumber } from "./schema/recover/unsubscribe-recover-for-phone-number";
import { getSampleTrackingCodes } from "./schema/shipment-tracking/get-sample-tracking-codes";
import { getTeamCarrierDistribution } from "./schema/shipment-tracking/get-team-carrier-distribution";
import { lookupTracking } from "./schema/shipment-tracking/lookup-tracking";
import { testTrackingSamples } from "./schema/shipment-tracking/test-tracking-samples";
import { createAbTest } from "./schema/shopper-agent/create-ab-test";
import { endAbTest } from "./schema/shopper-agent/end-ab-test";
import { getAbTest } from "./schema/shopper-agent/get-ab-test";
import { getActiveShopperAgentMerchants } from "./schema/shopper-agent/get-active-shopper-agent-merchants";
import { getCommerceAgentRevenue } from "./schema/shopper-agent/get-commerce-agent-revenue";
import { getImessageChannelExperimentAggregate } from "./schema/shopper-agent/get-imessage-channel-experiment-aggregate";
import { getIMessageChannelExperimentAnalytics } from "./schema/shopper-agent/get-imessage-channel-experiment-analytics";
import { getShopperAgentAbMerchants } from "./schema/shopper-agent/get-shopper-agent-ab-merchants";
import { getShopperAgentAnalytics } from "./schema/shopper-agent/get-shopper-agent-analytics";
import { getShopperAgentCapabilities } from "./schema/shopper-agent/get-shopper-agent-capabilities";
import { getShopperAgentEmbeddingBackfillFleet } from "./schema/shopper-agent/get-shopper-agent-embedding-backfill-fleet";
import { getShopperAgentFleetAnalytics } from "./schema/shopper-agent/get-shopper-agent-fleet-analytics";
import { getShopperAgentObservability } from "./schema/shopper-agent/get-shopper-agent-observability";
import { listAbTests } from "./schema/shopper-agent/list-ab-tests";
import { resolveShopperAgentObservability } from "./schema/shopper-agent/resolve-shopper-agent-observability";
import { getTeamSignupForms } from "./schema/signups/get-team-signup-forms";
import { getTeamsWithSignupForms } from "./schema/signups/get-teams-with-signup-forms";
import { migrateSignupForm } from "./schema/signups/migrate-signup-form";
import { setSpotlightsEnabled } from "./schema/signups/set-spotlights-enabled";

const marketingAgentPromptSchema = {
  createMarketingAgentPrompt,
  updateMarketingAgentPrompt,
  getAllMarketingAgentPrompts,
} as const;

const campaignTimelineSchema = { getCampaignTimelineData } as const;

const shopperAgentSchema = {
  getActiveShopperAgentMerchants,
  getCommerceAgentRevenue,
  getShopperAgentAbMerchants,
  getShopperAgentEmbeddingBackfillFleet,
  getShopperAgentAnalytics,
  getShopperAgentFleetAnalytics,
  getShopperAgentObservability,
  resolveShopperAgentObservability,
  getShopperAgentCapabilities,
  getIMessageChannelExperimentAnalytics,
  getImessageChannelExperimentAggregate,
  listAbTests,
  getAbTest,
  createAbTest,
  endAbTest,
} as const;

const conversationsSchema = {
  claimConversation,
  getConversationById,
  getQueueConversations,
  getAllQueueCounts,
  getConversationLogs,
  getConversationMessages,
  hasDiscountConfigured,
  releaseConversationClaim,
  renewConversationClaims,
} as const;

const messagesSchema = {
  fetchDiscountInfo,
  getReplyToEmail,
  adminSendDraftMessage,
  contractorSendDraftMessage,
  adminSendManualMessage,
  contractorSendManualMessage,
  flagDraftMessage,
  skipDraftMessage,
  regenerateDraftMessage,
} as const;

const permissionsSchema = {
  getCurrentMarketingPermissions,
  listMarketingPermissionUsers,
  setMarketingPermissionUser,
  removeMarketingPermissionUser,
} as const;

const recoverSchema = {
  getHumanQueueAgentActivity,
  getRecoverAnalytics,
  getRecoverAutomationMetrics,
  getRecoverInternalNotes,
  getRecoverMerchantNotes,
  getRecoverDailyMetrics,
  getRecoverDiscountSplitMetrics,
  getRecoverImessageMerchants,
  getRecoverTemplates,
  reportRecoverIssue,
  searchRecoverTeams,
  unsubscribeRecoverForPhoneNumber,
  getRecoverConversationReturn,
} as const;

const marketingAgentSplitsSchema = {
  getActiveMarketingAgentSplits,
  getMarketingAgentDecisionDetails,
  getMarketingAgentDecisions,
  getMarketingAgentKeyMetrics,
  getMarketingAgentDecisionTrends,
  getMarketingAgentTagDistribution,
  getMarketingAgentChannelDistribution,
  getMarketingAgentTriggerEventDistribution,
  getMarketingAgentWriterMessageTrends,
  getMarketingAgentLlmCostMetrics,
  getMarketingAgentLlmCostTrends,
  getMarketingAgentPerformanceMetrics,
  getMarketingAgentSplitTestRevenueStats,
  getMarketingAgentSplitTestMessageStats,
  getMarketingAgentSplitTestRevenueChart,
  getMarketingAgentSplitTestStatisticalSignificance,
} as const;

const emailTemplatesSchema = { debugEmailTemplate } as const;

const emailCorrectionsSchema = {
  listPendingEmailCorrections,
  resolveEmailCorrection,
} as const;

const shipmentTrackingSchema = {
  getTeamCarrierDistribution,
  getSampleTrackingCodes,
  lookupTracking,
  testTrackingSamples,
} as const;

const signupsSchema = {
  getTeamsWithSignupForms,
  getTeamSignupForms,
  migrateSignupForm,
  setSpotlightsEnabled,
} as const;

export const rpcDefinition = {
  ...emailTemplatesSchema,
  ...emailCorrectionsSchema,
  ...marketingAgentPromptSchema,
  ...campaignTimelineSchema,
  ...shopperAgentSchema,
  ...conversationsSchema,
  ...messagesSchema,
  ...permissionsSchema,
  ...recoverSchema,
  ...marketingAgentSplitsSchema,
  ...shipmentTrackingSchema,
  ...signupsSchema,
};

export type AdminMarketingRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
